import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

const STORAGE_ACCEPTED = 'mgm_email_discount_accepted';
const STORAGE_DISMISSED = 'mgm_email_discount_dismissed';
const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

class MgmEmailDiscountPopup extends HTMLElement {
  #abortController = new AbortController();
  #shownThisPage = false;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.querySelector('[data-mgm-email-discount-form]')?.addEventListener('submit', this.#onSubmit, { signal });
    this.querySelectorAll('[data-mgm-email-discount-close]').forEach((element) => {
      element.addEventListener('click', this.#closeAndRemember, { signal });
    });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('shopify:cart:lines-update', this.#onCartLinesUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #onCartLinesUpdate = () => {
    if (!this.#canShow()) return;

    const delay = Number(this.dataset.delay || 700);
    window.setTimeout(() => {
      if (this.#canShow()) this.#open();
    }, delay);
  };

  #canShow() {
    if (!this.dataset.discountCode || this.#shownThisPage) return false;
    if (localStorage.getItem(STORAGE_ACCEPTED) === 'true') return false;
    if (this.dataset.showOnce === 'true' && localStorage.getItem(STORAGE_DISMISSED) === 'true') return false;
    return true;
  }

  #open() {
    this.#shownThisPage = true;
    this.hidden = false;
    document.documentElement.classList.add('mgm-email-discount-open');
    this.querySelector('input[type="email"]')?.focus();
  }

  #close() {
    this.hidden = true;
    document.documentElement.classList.remove('mgm-email-discount-open');
  }

  #closeAndRemember = () => {
    localStorage.setItem(STORAGE_DISMISSED, 'true');
    this.#close();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.hidden) {
      this.#closeAndRemember();
    }
  };

  #onSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[name="email"]');
    const button = form.querySelector('button[type="submit"]');
    const error = this.querySelector('[data-mgm-email-discount-error]');
    const success = this.querySelector('[data-mgm-email-discount-success]');
    if (!(input instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

    error?.setAttribute('hidden', '');
    success?.setAttribute('hidden', '');

    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }

    button.disabled = true;

    try {
      const cart = await this.#applyDiscount(input.value.trim());
      localStorage.setItem(STORAGE_ACCEPTED, 'true');
      localStorage.setItem(STORAGE_DISMISSED, 'true');
      success?.removeAttribute('hidden');

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: [{ code: this.dataset.discountCode || '' }],
          promise: Promise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart) }),
        })
      );

      window.setTimeout(() => this.#close(), 850);
    } catch (submitError) {
      error?.removeAttribute('hidden');
      this.dispatchEvent(
        new CartErrorEvent({
          error: submitError instanceof Error ? submitError.message : 'Failed to apply discount',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button.disabled = false;
    }
  };

  async #applyDiscount(email) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: this.dataset.discountCode,
        attributes: {
          'Email discount signup': email,
          'Email discount code': this.dataset.discountCode,
        },
        sections: [CART_DRAWER_SECTION_ID],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    if (!response.ok) throw new Error('Discount could not be applied.');

    const cart = await response.json();
    const discountCode = this.dataset.discountCode || '';
    const appliedDiscount = cart.discount_codes?.find(
      (discount) => discount.code?.toLowerCase() === discountCode.toLowerCase() && discount.applicable
    );

    if (!appliedDiscount) {
      throw new Error('Discount code is not active or is not applicable.');
    }

    if (cart.sections?.[CART_DRAWER_SECTION_ID]) {
      morphSection(CART_DRAWER_SECTION_ID, cart.sections[CART_DRAWER_SECTION_ID], {
        mode: document.querySelector('theme-drawer#cart-drawer') ? 'hydration' : 'full',
      });
    }

    return cart;
  }
}

if (!customElements.get('mgm-email-discount-popup')) {
  customElements.define('mgm-email-discount-popup', MgmEmailDiscountPopup);
}
